import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { FaUserShield, FaPassport, FaChartLine, FaMoneyBillWave } from 'react-icons/fa';

// Import images
import heroImg1 from '../assets/ramb-hero-bg-1.png';
import heroImg2 from '../assets/ramb-hero-bg-2.png';
import educationImg from '../assets/education.jpeg';
import goldImg from '../assets/gold-plus.jpg';
import rambLogo from '../assets/logowithoutmoto.jpg';

const slides = [
    {
        id: 1,
        image: heroImg1,
        logo: rambLogo,
        subtitle: "Your Success is Our Interest",
        desc: "Reliable Asset Management & Business solutions tailored for you.",
        cta: "Contact Us",
        link: "contact" // internal section
    },
    {
        id: 2,
        image: heroImg2,
        title: "Flexible Loans",
        subtitle: "Empowering Your Dreams",
        desc: "Personal and business loans with competitive rates and easy approval.",
        cta: "View Loans",
        link: "/loans" // route
    },
    {
        id: 3,
        image: educationImg,
        title: "Education Support",
        subtitle: "Investing in the Future",
        desc: "Comprehensive financial support for your educational journey.",
        cta: "Learn More",
        link: "/education-support"
    },
    {
        id: 4,
        image: goldImg,
        title: "Gold Plus",
        subtitle: "Secure & Reliable",
        desc: "Trusted gold financing and asset management services.",
        cta: "Explore Gold Plus",
        link: "/gold-plus"
    }
];

export default function HeroCarousel() {
    const [index, setIndex] = useState(0);
    const navigate = useNavigate();
    const width = useWindowWidth();

    useEffect(() => {
        const timer = setInterval(() => {
            setIndex((prev) => (prev + 1) % slides.length);
        }, 6000); // 6 seconds per slide
        return () => clearInterval(timer);
    }, []);

    const handleCTA = (link) => {
        if (link === 'contact') {
            const el = document.getElementById('contact');
            if (el) el.scrollIntoView({ behavior: 'smooth' });
        } else {
            navigate(link);
        }
    };

    return (
        <div className="hero-carousel" style={{ position: 'relative', height: '100vh', width: '100%', overflow: 'hidden', background: '#000' }}>
            <AnimatePresence mode="wait">
                <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 1.05 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 1 }}
                    style={{
                        position: 'absolute',
                        inset: 0,
                        backgroundImage: `url(${slides[index].image})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                    }}
                >
                    {/* Overlay */}
                    <div style={{
                        position: 'absolute',
                        inset: 0,
                        background: 'linear-gradient(to right, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.3) 100%)',
                    }} />
                </motion.div>
            </AnimatePresence>

            <div className="carousel-content">
                <div className="carousel-text-area">
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={index}
                            initial={{ opacity: 0, x: -50 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: 50 }}
                            transition={{ duration: 0.6, delay: 0.2 }}
                            className="carousel-inner-content"
                        >
                            <motion.h4 className="carousel-subtitle">
                                {slides[index].subtitle}
                            </motion.h4>

                            {slides[index].logo ? (
                                <motion.img
                                    src={slides[index].logo}
                                    alt="RAMB Logo"
                                    className="carousel-logo-img"
                                />
                            ) : (
                                <motion.h1 className="carousel-title">
                                    {slides[index].title}
                                </motion.h1>
                            )}

                            <motion.p className="carousel-desc">
                                {slides[index].desc}
                            </motion.p>

                            <motion.button
                                onClick={() => handleCTA(slides[index].link)}
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                className="carousel-btn"
                            >
                                {slides[index].cta}
                            </motion.button>
                        </motion.div>
                    </AnimatePresence>
                </div>
            </div>

            {/* Indicators */}
            <div style={{
                position: 'absolute',
                bottom: '40px',
                left: '50%',
                transform: 'translateX(-50%)',
                display: 'flex',
                gap: '12px',
                zIndex: 20
            }}>
                {slides.map((_, i) => (
                    <div
                        key={i}
                        onClick={() => setIndex(i)}
                        style={{
                            width: i === index ? '30px' : '10px',
                            height: '10px',
                            borderRadius: '5px',
                            background: i === index ? 'var(--blue1)' : 'rgba(255,255,255,0.4)',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease'
                        }}
                    />
                ))}
            </div>

            {/* Floating Portal Buttons (Right Side) */}
            <div className="hero-floating-portals" style={{
                position: 'absolute',
                right: width > 768 ? '40px' : '50%',
                top: width > 768 ? '50%' : '80px', /* Move to top on mobile to avoid blocking central content/logo */
                bottom: width > 768 ? 'auto' : 'auto',
                transform: width > 768 ? 'translateY(-50%)' : 'translateX(50%)',
                display: 'flex',
                flexDirection: width > 768 ? 'column' : 'column',
                gap: width > 768 ? '16px' : '8px',
                zIndex: 30,
                width: width > 768 ? 'auto' : '90%',
                maxWidth: width > 768 ? 'none' : '360px'
            }}>
                <motion.button
                    initial={{ opacity: 0, x: 50 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.2, ease: "easeOut" }}
                    whileHover={{ scale: 1.02, background: 'rgba(37, 99, 235, 0.45)', borderColor: 'rgba(37, 99, 235, 0.6)' }}
                    whileTap={{ scale: 0.97 }}
                    onClick={() => window.open("https://goldplus.rambgroup.com/customer/login", "_blank")}
                    style={{ ...floatingBtnStyle, background: 'rgba(255, 255, 255, 0.15)', borderLeft: '4px solid #2563eb' }}
                >
                    <div style={{ ...iconBgStyle, color: '#2563eb', marginTop: width > 768 ? '2px' : '0' }}>
                        <FaUserShield size={width > 768 ? 22 : 18} />
                    </div>
                    <div style={{ textAlign: 'left', width: '100%' }}>
                        <div style={{ fontSize: width > 768 ? '16px' : '14px', fontWeight: 'bold', marginBottom: width > 768 ? '6px' : '4px' }}>RAMB Customer Portal</div>
                        {width > 768 && (
                            <ul style={{ fontSize: '12px', margin: 0, paddingLeft: '15px', opacity: 0.85, display: 'flex', flexDirection: 'column', gap: '3px' }}>
                                <li>Pawning accounts management</li>
                                <li>Loans management</li>
                                <li>Requesting new loans</li>
                                <li>Make part payments & more</li>
                            </ul>
                        )}
                    </div>
                </motion.button>

                <motion.button
                    initial={{ opacity: 0, x: 50 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1, duration: 0.2, ease: "easeOut" }}
                    whileHover={{ scale: 1.02, background: 'rgba(13, 148, 136, 0.45)', borderColor: 'rgba(13, 148, 136, 0.6)' }}
                    whileTap={{ scale: 0.97 }}
                    onClick={() => window.open("https://visa.rambgroup.com/", "_blank")}
                    style={{ ...floatingBtnStyle, background: 'rgba(255, 255, 255, 0.15)', borderLeft: '4px solid #0f766e' }}
                >
                    <div style={{ ...iconBgStyle, color: '#0f766e' }}>
                        <FaPassport size={width > 768 ? 22 : 18} />
                    </div>
                    <div style={{ textAlign: 'left', width: '100%' }}>
                        <div style={{ fontSize: width > 768 ? '13px' : '11px', opacity: 0.8 }}>Explore</div>
                        <div style={{ fontSize: width > 768 ? '16px' : '14px', fontWeight: 'bold' }}>RAMB Global VISA</div>
                    </div>
                </motion.button>

                <motion.button
                    initial={{ opacity: 0, x: 50 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.2, duration: 0.2, ease: "easeOut" }}
                    whileHover={{ scale: 1.02, background: 'rgba(217, 119, 6, 0.45)', borderColor: 'rgba(217, 119, 6, 0.6)' }}
                    whileTap={{ scale: 0.97 }}
                    onClick={() => window.open("https://goldplus.rambgroup.com/gold/register", "_blank")}
                    style={{ ...floatingBtnStyle, background: 'rgba(255, 255, 255, 0.15)', borderLeft: '4px solid #d97706' }}
                >
                    <div style={{ ...iconBgStyle, color: '#d97706' }}>
                        <FaChartLine size={width > 768 ? 22 : 18} />
                    </div>
                    <div style={{ textAlign: 'left', width: '100%' }}>
                        <div style={{ fontSize: width > 768 ? '13px' : '11px', opacity: 0.8 }}>Register for</div>
                        <div style={{ fontSize: width > 768 ? '16px' : '14px', fontWeight: 'bold' }}>Gold Plus Account Openings</div>
                    </div>
                </motion.button>

                <motion.button
                    initial={{ opacity: 0, x: 50 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3, duration: 0.2, ease: "easeOut" }}
                    whileHover={{ scale: 1.02, background: 'rgba(234, 179, 8, 0.35)', borderColor: 'rgba(234, 179, 8, 0.6)' }}
                    whileTap={{ scale: 0.97 }}
                    onClick={() => window.open("https://wupos2.westernunion.com/web-ui/login", "_blank")}
                    style={{ ...floatingBtnStyle, background: 'rgba(255, 255, 255, 0.15)', borderLeft: '4px solid #eab308' }}
                >
                    <div style={{ ...iconBgStyle, color: '#ca8a04' }}>
                        <FaMoneyBillWave size={width > 768 ? 22 : 18} />
                    </div>
                    <div style={{ textAlign: 'left', width: '100%' }}>
                        <div style={{ fontSize: width > 768 ? '13px' : '11px', opacity: 0.8 }}>Info</div>
                        <div style={{ fontSize: width > 768 ? '16px' : '14px', fontWeight: 'bold' }}>Western Union</div>
                    </div>
                </motion.button>
            </div>
        </div>
    );
}

const floatingBtnStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    background: 'rgba(255, 255, 255, 0.1)',
    backdropFilter: 'blur(16px)',
    padding: '8px 12px', /* Slimmer padding to keep simple */
    borderRadius: '16px',
    cursor: 'pointer',
    color: 'white', /* Ensure white text contrasts heavily against bg images */
    boxShadow: 'none', /* Removed the dark shadow causing the dark margin */
    width: '100%',
    minWidth: '240px',
    justifyContent: 'flex-start',
    fontFamily: 'inherit',
    border: 'none', /* Completely remove extra borders */
    transition: 'background 0.1s ease-out, border-color 0.1s ease-out, transform 0.1s cubic-bezier(0.4, 0, 0.2, 1)'
};

// Use dynamic approach for values that need to read window.innerWidth repeatedly on resize
// So we use JS event listener to force a re-render so innerWidth updates properly

export function useWindowWidth() {
    const [width, setWidth] = useState(window.innerWidth);
    useEffect(() => {
        const handleResize = () => setWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);
    return width;
}

const iconBgStyle = {
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    background: 'rgba(255,255,255,0.9)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexShrink: 0,
    boxShadow: '0 4px 10px rgba(0,0,0,0.08)'
};

const listItemStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    lineHeight: 1.4
};

const bulletStyle = {
    color: 'rgba(255,255,255,0.7)',
    fontSize: '16px',
    lineHeight: 1
};
